import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, FileCheck, Map, Handshake, Search, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { CtaSection } from "../components/CtaSection";
import { CinematicBreak } from "../components/CinematicBreak";
import { HmoLicenceChecker } from "../components/HmoLicenceChecker";
import { HmoYieldSnapshot } from "../components/HmoYieldSnapshot";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC } from "../data/site";

const WHO = [
  {
    icon: TrendingUp,
    title: "Investors & Landlords",
    text: "Those considering an HMO purchase, converting a property into one, or wanting existing shared properties run compliantly.",
  },
  {
    icon: Users,
    title: "Tenants in Shared Homes",
    text: "People living in shared housing who want to understand their rights and what a properly managed HMO should look like.",
  },
  {
    icon: HomeIcon,
    title: "Owners of Larger Houses",
    text: "Owners of sizeable period properties exploring whether multi-let is a suitable route for their building.",
  },
];

const LICENSING = [
  {
    num: "01",
    title: "Mandatory HMO Licensing",
    text: "Across England, a licence is required for any HMO occupied by five or more people forming more than one household, regardless of the number of storeys. Licences are issued by the local council and come with conditions covering safety, facilities and management standards.",
  },
  {
    num: "02",
    title: "Additional Licensing",
    text: "Many councils go further and require smaller HMOs — sometimes any shared house in the area — to be licensed too. The rules differ from one local authority to the next, so the first step is always to check the requirements where the property sits.",
  },
];

const PLANNING = [
  {
    title: "Use Class C4",
    text: "A small HMO (three to six sharers) usually falls within use class C4. Moving between a standard dwelling and C4 is often permitted development — but not everywhere.",
  },
  {
    title: "Article 4 Directions",
    text: "Many councils with high concentrations of shared housing have removed these automatic rights, meaning planning permission is needed to create an HMO. We help you check before you commit.",
  },
  {
    title: "Larger HMOs — Sui Generis",
    text: "Properties with seven or more occupants generally fall outside C4 and require planning permission in their own right, alongside licensing.",
  },
];

const OFFER = [
  {
    icon: Search,
    num: "01",
    title: "HMO Sourcing",
    text: "We identify properties suited to compliant multi-let use — the right size, layout and location — matched to your goals and the local licensing landscape.",
  },
  {
    icon: ShieldCheck,
    num: "02",
    title: "Compliance Guidance",
    text: "Clear, practical guidance on licensing applications, safety standards and management duties, with solicitors and specialists introduced where formal advice is needed.",
  },
  {
    icon: Handshake,
    num: "03",
    title: "Landlord Support",
    text: "Ongoing support for HMO landlords: from refurbishment coordination through our development company to tenant finding with trusted estate agents.",
  },
];

const HmoServices = () => {
  usePageMeta(
    "HMO Services | Elin's Consultancy",
    "HMO sourcing, licensing and compliance guidance, and landlord support — clear advice on Houses in Multiple Occupation across England."
  );
  return (
    <Layout>
      <PageHero
        eyebrow="HMO Services"
        lines={[
          "HMO Property,",
          <>
            <em className="italic font-normal text-gold-soft">Handled Properly</em>
          </>,
        ]}
        text="Houses in Multiple Occupation can be rewarding but carry real regulatory weight. We help landlords and investors source, license and manage HMOs with confidence."
        image={CINEMATIC.wideSalon}
        imageAlt="A spacious, well-kept shared living space with generous natural light"
      />

      <section data-testid="hmo-what" className="bg-cream">
        <div className="wrap-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <Reveal>
            <p className="eyebrow text-gold-dark">The Basics</p>
            <h2 className="font-serif text-4xl sm:text-[44px] font-medium text-forest leading-[1.08] mt-5 mb-6">
              What Is an HMO?
            </h2>
            <div className="space-y-5 text-[14.5px] leading-[1.8] text-sage max-w-lg">
              <p>
                A House in Multiple Occupation is a property rented out to at least three people who
                are not from one household — for example a group of friends or individual tenants —
                who share facilities such as a kitchen or bathroom.
              </p>
              <p>
                HMOs play an important role in UK housing, and they are more heavily regulated than
                standard rentals. Licensing, planning rules and management standards all apply —
                which is precisely where clear, independent guidance earns its keep.
              </p>
            </div>
          </Reveal>
          <div className="space-y-0">
            {WHO.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className={`flex gap-6 py-7 ${i > 0 ? "border-t border-forest/12" : ""}`}>
                  <span className="w-12 h-12 shrink-0 rounded-full border border-gold flex items-center justify-center">
                    <w.icon size={20} strokeWidth={1.4} className="text-gold-dark" />
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-forest">{w.title}</h3>
                    <p className="text-[13.5px] leading-[1.7] text-sage mt-2">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="hmo-licensing" className="bg-cream-2">
        <SectionHead
          eyebrow="Licensing"
          title="HMO Licensing, Explained Plainly"
          text="Two layers of licensing can apply to an HMO in England. Getting this wrong can mean significant penalties, so it pays to check early."
        />
        <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-1 md:grid-cols-2 gap-6">
          {LICENSING.map((l, i) => (
            <Reveal key={l.num} delay={i * 0.12}>
              <article data-testid={`hmo-licensing-${l.num}`} className="bg-cream-3 border border-forest/10 p-8 sm:p-12 h-full transition-colors duration-500 hover:border-gold/60">
                <div className="flex items-center gap-4">
                  <span className="font-serif italic text-xl text-gold-dark">{l.num}</span>
                  <span className="w-10 h-px bg-gold/50" />
                  <FileCheck size={24} strokeWidth={1.3} className="text-gold" />
                </div>
                <h3 className="font-serif text-3xl font-medium text-forest mt-5 mb-4">{l.title}</h3>
                <p className="text-sm leading-[1.75] text-sage">{l.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <HmoLicenceChecker />

      <HmoYieldSnapshot />

      <CinematicBreak
        image={CINEMATIC.streetCrescent}
        alt="A golden-hour residential London crescent lined with period townhouses"
        caption="The Local Picture Matters"
        quote="HMO rules change from one council to the next. Always check the local position before you buy or convert."
      />

      <section data-testid="hmo-planning" className="bg-cream">
        <SectionHead
          eyebrow="Planning Permission"
          title="When Planning Permission Comes Into Play"
          text="Licensing and planning are separate regimes — an HMO can need one, both, or neither."
        />
        <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-1 md:grid-cols-3">
          {PLANNING.map((p, i) => (
            <Reveal
              key={p.title}
              delay={i * 0.12}
              className={`py-8 md:py-0 md:px-10 ${i > 0 ? "border-t md:border-t-0 md:border-l border-forest/15" : ""} ${i === 0 ? "md:pl-0" : ""}`}
            >
              <Map size={30} strokeWidth={1.3} className="text-gold" />
              <h3 className="font-serif text-2xl font-semibold text-forest mt-5 mb-2.5">{p.title}</h3>
              <p className="text-[13.5px] text-sage leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="hmo-offer" className="bg-forest text-cream relative grain">
        <SectionHead
          dark
          eyebrow="How We Help"
          title="HMO Support From Elin's Consultancy"
          text="Practical, independent support at every stage of the HMO journey."
        />
        <div className="wrap-x relative z-[2] pb-20 sm:pb-24 grid grid-cols-1 md:grid-cols-3 gap-y-12">
          {OFFER.map((o, i) => (
            <Reveal key={o.num} delay={i * 0.1} className="px-6 text-center">
              <div className="w-[68px] h-[68px] rounded-full border border-gold mx-auto flex items-center justify-center">
                <o.icon size={26} strokeWidth={1.3} className="text-gold-soft" />
              </div>
              <p className="text-[11px] tracking-[0.3em] text-gold font-bold mt-5">{o.num}</p>
              <h3 className="font-serif text-2xl font-semibold mt-2 mb-3">{o.title}</h3>
              <p className="text-[13px] leading-[1.7] text-cream/55 max-w-xs mx-auto">{o.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="hmo-investors" className="bg-cream-2 border-t border-forest/10">
        <div className="wrap-x py-16 sm:py-20 max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="eyebrow text-gold-dark">For Investors</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest mt-5 mb-6">
              Why Investors Consider HMOs
            </h2>
            <p className="text-[14.5px] leading-[1.8] text-sage">
              A single HMO generates several rental incomes from one property, which is why many
              investors find the model attractive — alongside steady demand for good-quality shared
              homes. Higher income potential comes with heavier regulation and more involved
              management. As always, we never promise returns: we offer a careful process, honest
              advice and the right professionals beside you.
            </p>
            <Link to="/contact" data-testid="hmo-enquire-button" className="btn-forest mt-9">
              Discuss an HMO Enquiry <ArrowRight size={14} strokeWidth={2.2} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title={
          <>
            Considering an
            <br />
            <em className="italic font-normal text-gold-soft">HMO Investment?</em>
          </>
        }
        text="Whether you're buying your first HMO or regularising an existing one, start with a conversation — we'll map out the licensing, planning and management steps with you."
      />
    </Layout>
  );
};

export default HmoServices;
